import {
    isArray,
    isString,
    isEmpty,
    isNull,
    flattenDeep,
    filter,
    isUndefined,
    isNil,
    chunk
} from 'lodash/fp';
import { quoteIfNeeded } from './sql';

export const clean = (a) =>
    filter((e) => (isString(e) ? e.trim() !== '' : !(isNull(e) || isUndefined(e))), flattenDeep(a));

export function identifier(...names) {
    return clean(names).map(quoteIfNeeded).join('.');
}

export function placeholder(value) {
    return `"{{${value}}}"`;
}

export function value(val) {
    return isArray(val) ? `(${val.map(value).join(', ')})` : isString(val) ? `'${val}'` : val;
}

export function formula(fn, column, arg) {
    return arg ? `${fn}(${column}, ${arg})` : `${fn}(${column})`;
}

export function select(...columns) {
    return `SELECT ${clean(columns).join(', ')}`;
}

export function columnExpression(name, ...modifiers) {
    const clauses = clean(modifiers).join(' ');

    return clauses.length > 0 ? `${name} ${clauses}` : name;
}

export function rename(...aliases) {
    const renames = clean(aliases);

    return isEmpty(renames) ? null : `RENAME (${renames.join(', ')})`;
}

export function exclude(...columns) {
    const excludes = clean(columns);

    return isEmpty(excludes) ? null : `EXCLUDE (${excludes.join(', ')})`;
}

export function from(...args) {
    return `FROM ${clean(args).join(', ')}`;
}

export function betweenOp(column, leftRange, rightRange) {
    return `${column} BETWEEN ${leftRange} AND ${rightRange}`;
}

export function condition(lhs, op, rhs) {
    return isNil(rhs) || rhs === '' ? `${lhs} ${op}` : `${lhs} ${op} ${rhs}`;
}

export function isNullCondition(column) {
    return `${column} IS NULL`;
}

export function and(...args) {
    const conditions = clean(args);

    return isEmpty(conditions) ? null : conditions.join(' AND ');
}

export function not(sql) {
    return `NOT ${sql}`;
}

export function or(...args) {
    const conditions = clean(args);

    return isEmpty(conditions) ? null : conditions.join(' OR ');
}

export function where(...args) {
    const conditions = clean(args);

    return isEmpty(conditions) ? null : `WHERE ${and(...conditions)}`;
}

export function parens(sql) {
    return isNil(sql) ? sql : `(${sql})`;
}

export function alias(name, alias) {
    return !isEmpty(alias) && name !== alias ? `${name} AS ${alias}` : name;
}

export function sql(...parts) {
    return clean(parts).join(' ');
}

export function join(type, table, ...conditions) {
    return `${type ? `${type} ` : ''}JOIN ${table}${
        conditions.length > 0 ? ` ON ${clean(conditions).join(' AND ')}` : ''
    }`;
}

export function group(...args) {
    const groups = clean(args);

    return isEmpty(groups) ? null : `GROUP BY ${groups.join(', ')}`;
}

export function groups(n) {
    const groupKeys = Array(n)
        .fill(null)
        .map((_, i) => i + 1);

    return `GROUP BY ${groupKeys.join(', ')}`;
}

export function having(...args) {
    const conditions = clean(args);

    return isEmpty(conditions) ? null : `HAVING ${and(...conditions)}`;
}

export function sort(column, direction) {
    return `${column}${direction ? ` ${direction}` : ''}`;
}

export function order(...args) {
    const sorts = clean(args);

    return isEmpty(sorts) ? null : `ORDER BY ${clean(sorts).join(', ')}`;
}

export function limit(n) {
    return isNull(n) || isUndefined(n) ? null : `LIMIT ${n}`;
}

export function cte(...args) {
    const pairs = chunk(2, args).map(([alias, sql]) => `${alias} AS (${sql})`);

    return `WITH ${pairs.join(', ')}`;
}

export function distinct(value) {
    return `DISTINCT ${value}`;
}

export const rowCountColumn = () => 'COUNT(0) OVER ()';

export const count = (column = '*') => `COUNT(${column})`;

export function union(...args) {
    return clean(args).join(' UNION ');
}

export function exists(sql) {
    return `EXISTS ${sql}`;
}

export function inOperator(column, args) {
    return isEmpty(args) ? null : `${column} IN (${args.map((arg) => value(arg)).join(', ')})`;
}

export const generateRangeConditions = (filter, column) => {
    if (isNil(filter?.rangeFrom) && isNil(filter?.rangeTo)) {
        return condition(column, 'IS NOT NULL');
    }

    const { rangeFrom, rangeTo } = filter;

    return and(
        !isNil(rangeFrom) ? condition(column, '>=', value(rangeFrom)) : null,
        !isNil(rangeTo) ? condition(column, '<=', value(rangeTo)) : null
    );
};
